import React from 'react';
import { Col, Placeholder, Row } from 'react-bootstrap';
import CardLayout from '../../../../../shared/components/card/CardLayout';

const CardHeaderSkeleton = ({ withSubtitle = false, withButton = false }) => (
    <div className="d-flex justify-content-between align-items-start mb-3">
        <div className="d-flex align-items-start flex-grow-1">
            <Placeholder className="skeleton skeleton-header-icon mr-2" />
            <div className="flex-grow-1" style={{ maxWidth: 260 }}>
                <Placeholder xs={7} className="skeleton d-block" />
                {withSubtitle && <Placeholder xs={10} className="skeleton d-block mt-2" />}
            </div>
        </div>
        {withButton && <Placeholder className="skeleton skeleton-outline-btn outline-btn-lg" />}
    </div>
);

const StatSkeleton = () => (
    <div>
        <Placeholder className="skeleton skeleton-stat-value skeleton-stat-value-xl d-block" />
        <Placeholder className="skeleton skeleton-stat-label d-block mt-2" />
    </div>
);

interface IDashStatsCardSkeletonProps {
    stats?: number;
    withSubtitle?: boolean;
    withButton?: boolean;
    withImages?: boolean;
    images?: number;
    withProgress?: boolean;
    className?: string;
}

export function DashStatsCardSkeleton({
    stats = 3,
    withSubtitle = false,
    withButton = false,
    withImages = false,
    images = 3,
    withProgress = false,
    className = 'mb-3',
}: IDashStatsCardSkeletonProps) {
    return (
        <CardLayout classCard={className}>
            <Placeholder as="div" animation="glow" aria-hidden="true">
                <CardHeaderSkeleton withSubtitle={withSubtitle} withButton={withButton} />
                <Row className="align-items-center my-3">
                    <Col className="d-flex flex-wrap" style={{ rowGap: 16 }}>
                        {Array.from({ length: stats }).map((_, i) => (
                            <div key={i} className="col-6 col-lg px-0">
                                <StatSkeleton />
                            </div>
                        ))}
                    </Col>
                    {withImages && (
                        <Col md="auto" className="d-none d-md-flex">
                            {Array.from({ length: images }).map((_, i) => (
                                <Placeholder key={i} className="skeleton skeleton-dash-image ml-2" />
                            ))}
                        </Col>
                    )}
                </Row>
                {withProgress && (
                    <>
                        <div className="d-flex justify-content-between mb-2">
                            <Placeholder xs={3} className="skeleton" />
                            <Placeholder xs={3} className="skeleton" />
                            <Placeholder xs={1} className="skeleton" />
                        </div>
                        <Placeholder className="skeleton skeleton-progress d-block" />
                    </>
                )}
            </Placeholder>
        </CardLayout>
    );
}

export function DashUsersSkeleton({ users = 2, footer = 'between' }: { users?: number; footer?: 'between' | 'end' }) {
    return (
        <Placeholder as="div" animation="glow" aria-hidden="true">
            <div className="d-flex align-items-center mb-3">
                <Placeholder className="skeleton skeleton-header-icon mr-2" />
                <Placeholder xs={6} className="skeleton" />
            </div>
            <div className="d-flex flex-wrap" style={{ gap: 20 }}>
                {Array.from({ length: users }).map((_, i) => (
                    <div key={i} className="d-flex align-items-center">
                        <Placeholder className="skeleton skeleton-avatar" />
                        <div className="ml-2">
                            <Placeholder className="skeleton skeleton-table-name d-block" />
                            <Placeholder className="skeleton skeleton-stat-label d-block mt-1" />
                        </div>
                    </div>
                ))}
            </div>
            {footer === 'between' ? (
                <div className="d-flex justify-content-between mt-3">
                    <Placeholder xs={4} className="skeleton" />
                    <Placeholder xs={2} className="skeleton" />
                </div>
            ) : (
                <div className="d-flex justify-content-end mt-3" style={{ gap: 16 }}>
                    <Placeholder xs={2} className="skeleton" />
                    <Placeholder xs={3} className="skeleton" />
                </div>
            )}
        </Placeholder>
    );
}

export function BriefingDashCardSkeleton() {
    return (
        <CardLayout classCard="mb-3 mb-md-0">
            <Placeholder as="div" animation="glow" aria-hidden="true">
                <div className="d-flex align-items-center mb-3">
                    <Placeholder className="skeleton skeleton-header-icon mr-2" />
                    <Placeholder xs={3} className="skeleton" />
                </div>
                <Row>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Col key={i} lg={2} className="col-6 px-0 mb-2 mb-lg-0 d-flex flex-column align-items-center">
                            <Placeholder className="skeleton skeleton-stat-value" />
                            <Placeholder className="skeleton skeleton-stat-label mt-2" />
                        </Col>
                    ))}
                </Row>
            </Placeholder>
        </CardLayout>
    );
}

// Cijela stranica dok se ACL ne učita
export function CampaignDashboardSkeleton() {
    return (
        <div className="row d-lg-flex d-block mb-5 mb-md-0 pb-5 pb-md-0">
            <Col xs={12} className="mb-3">
                <BriefingDashCardSkeleton />
            </Col>
            <Col lg={7}>
                <DashStatsCardSkeleton stats={4} withSubtitle withButton withImages withProgress />
            </Col>
            <Col lg={5}>
                <CardLayout classCard="mb-3 mt-3 mt-md-0">
                    <DashUsersSkeleton />
                </CardLayout>
                <DashStatsCardSkeleton stats={3} withSubtitle className="" />
            </Col>
        </div>
    );
}
